// This service handles communication with the OpenAI API

// You'll need to add your OpenAI API key to your .env file
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY

export async function getRecommendations(dogInfo, breedInfo) {
  try {
    const { breed, age, weight, healthConditions } = dogInfo

    // Construct a prompt for the OpenAI API
    const prompt = `
      You are an expert pet nutritionist specializing in dog diets.
      
      Please provide food recommendations for a ${age}-year-old ${breed} ${weight ? `weighing ${weight} kg` : ""}.
      ${healthConditions.length > 0 ? `The dog has the following health conditions: ${healthConditions.join(", ")}.` : ""}
      ${breedInfo ? `Additional breed information: ${JSON.stringify(breedInfo)}` : ""}
      
      Provide 3 specific commercial dog food recommendations with:
      1. Brand and product name
      2. Key ingredients (list 3-5 main ingredients)
      3. Why it's suitable for this specific dog (2-3 benefits)
      4. Feeding guide based on the dog's weight
      
      Also include:
      - A homemade food alternative recipe suitable for this dog
      - 2-3 general feeding guidelines or tips
      
      Format the response as a JSON object with the following structure:
      {
        "foods": [
          {
            "name": "Brand Product Name",
            "ingredients": "Ingredient 1, Ingredient 2, Ingredient 3",
            "benefits": "Benefit 1, Benefit 2",
            "feedingGuide": "Feeding instructions"
          }
        ],
        "homemade": ["Recipe line 1", "Recipe line 2"],
        "feedingGuidelines": ["Guideline 1", "Guideline 2"]
      }
    `

    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: "gpt-4",
        messages: [
          {
            role: "system",
            content:
              "You are an expert pet nutritionist specializing in dog diets. Provide accurate and structured dog food recommendations.",
          },
          {
            role: "user",
            content: prompt,
          },
        ],
        temperature: 0.7,
        max_tokens: 1000,
      }),
    })

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`)
    }

    const data = await response.json()
    const content = data.choices[0].message.content

    // Parse the JSON response
    try {
      return JSON.parse(content)
    } catch (e) {
      console.error("Failed to parse OpenAI response as JSON:", e)

      // Fallback to a simple parsing approach if JSON parsing fails
      return parseUnstructuredResponse(content)
    }
  } catch (error) {
    console.error("Error getting recommendations:", error)
    throw error
  }
}

// Fallback parser in case the AI doesn't return proper JSON
function parseUnstructuredResponse(text) {
  const foods = []
  let homemade = []
  let feedingGuidelines = []

  // Simple parsing logic - this is a fallback
  if (text.includes("Recommended Dog Foods") || text.includes("Food Recommendations")) {
    // Try to extract food recommendations
    const foodMatches = text.match(
      /(\d️⃣|\d\.)[\s\n]+(.*?)[\s\n]+.*?Key Ingredients:[\s\n]+(.*?)[\s\n]+.*?Why It's Good:[\s\n]+(.*?)(?=\d️⃣|\d\.|Homemade|Feeding|$)/gs,
    )

    if (foodMatches) {
      foodMatches.forEach((match) => {
        const nameMatch = match.match(/(\d️⃣|\d\.)[\s\n]+(.*?)[\s\n]+/)
        const ingredientsMatch = match.match(/Key Ingredients:[\s\n]+(.*?)[\s\n]+/)
        const benefitsMatch = match.match(/Why It's Good:[\s\n]+(.*?)[\s\n]+/)
        const feedingMatch = match.match(/Feeding Guide:[\s\n]+(.*?)(?=\n|$)/)

        if (nameMatch && ingredientsMatch && benefitsMatch) {
          foods.push({
            name: nameMatch[2].trim(),
            ingredients: ingredientsMatch[1].trim(),
            benefits: benefitsMatch[1].trim(),
            feedingGuide: feedingMatch ? feedingMatch[1].trim() : "",
          })
        }
      })
    }

    // Try to extract homemade alternatives
    const homemadeMatch = text.match(/Homemade Alternative[s]?:[\s\n]+(.*?)(?=Feeding|$)/s)
    if (homemadeMatch) {
      homemade = homemadeMatch[1]
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line && !line.startsWith("Homemade"))
    }

    // Try to extract feeding guidelines
    const guidelinesMatch = text.match(/Feeding Tip[s]?:[\s\n]+(.*?)(?=$)/s)
    if (guidelinesMatch) {
      feedingGuidelines = guidelinesMatch[1]
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line && !line.startsWith("-") && !line.startsWith("Feeding"))
    }
  }

  return {
    foods:
      foods.length > 0
        ? foods
        : [
            {
              name: "Could not parse specific recommendations",
              ingredients: "Please check the raw response",
              benefits: "Please check the raw response",
              feedingGuide: "",
            },
          ],
    homemade: homemade.length > 0 ? homemade : ["Could not parse homemade alternatives"],
    feedingGuidelines: feedingGuidelines.length > 0 ? feedingGuidelines : ["Could not parse feeding guidelines"],
  }
}

